import { useCallback, useEffect, useState } from "react";

import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";

import FilterAlt from "@mui/icons-material/FilterAlt";

import FormField from "../../../components/FormField";
import TransferList from "../../../components/TransferList";
import NitroService from "../../../utils/nitro.service";
import { useNitroStore } from "../../../store/Store";
import { useIsMobile } from "../../../hooks/useIsMobile";

import UnsavedConfirmation from "../../../components/unsavedConfirmation";
import LoadingIcon from "../../../components/Loading";
import CloseIcon from "../../../components/CloseIcon";

const FilterOptions = ({ close, onFooterSelect }: { close: () => void; onFooterSelect?: (tab: any) => void }) => {
  const isMobile = useIsMobile(768);

  const [isLoading, setIsLoading] = useState(true);
  const [
    priorityProducts,
    setPriorityProducts,
    uom,
    setUom,
    currency,
    setCurrency,
  ] = useNitroStore((state) => [
    state.priorityProducts,
    state.setPriorityProducts,
    state.uom,
    state.setUom,
    state.currency,
    state.setCurrency,
  ]);

  const [tempUom, setTempUom] = useState<string>(uom || "");
  const [tempCurrency, setTempCurrency] = useState<string>(currency || "");

  const [productFilter, setProductFilter] = useState("");
  const [products, setProducts] = useState<
    {
      value: string;
      selected: boolean;
    }[]
  >([]);

  const loadProducts = useCallback(async () => {
    try {
      const result = await NitroService.getProducts();
      setProducts(
        result.map((p) => {
          let selected = false;
          if (priorityProducts.includes(p.name)) {
            selected = true;
          }
          return {
            value: p.name,
            selected,
          };
        })
      );
    } catch (err) {
      console.error(err);
    }
  }, [priorityProducts]);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      await loadProducts();
    } catch (err) {
      console.error(err);
    }
    setIsLoading(false);
  }, [loadProducts]);

  useEffect(() => {
    load();
  }, [load, loadProducts]);

  const save = () => {
    setPriorityProducts(products.filter((p) => p.selected).map((p) => p.value));
    setUom(tempUom);
    setCurrency(tempCurrency);

    if (isMobile && onFooterSelect) {
      onFooterSelect('origins');
    }
    close();
  };

  const closeSettings = () => {
    if (hasChange) {
      return setConfirmationPopupOpen(true);
    }

    if (isMobile && onFooterSelect) {
      onFooterSelect('origins');
    }
    return close();
  };

  const [confirmationPopupOpen, setConfirmationPopupOpen] = useState(false);
  const [hasChange, setHasChange] = useState(false);
  useEffect(() => {
    if (tempUom !== uom || tempCurrency !== currency) {
      return setHasChange(true);
    }

    const currentPriorityProducts = products.filter((p) => p.selected);
    const addedPriorityProducts = currentPriorityProducts.filter(
      (p) => !priorityProducts.find((pp) => pp === p.value)
    );
    const removedPriorityProducts = priorityProducts.filter(
      (p) => !currentPriorityProducts.find((cp) => cp.value === p)
    );
    if (addedPriorityProducts.length > 0) {
      return setHasChange(true);
    }
    if (removedPriorityProducts.length > 0) {
      return setHasChange(true);
    }

    return setHasChange(false);
  }, [tempUom, tempCurrency, products, uom, currency, priorityProducts]);

  return (
    <div
      id="filter-options-panel"
      style={{
        width: "100%",
        height: "90%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          position: "absolute",
          right: 10,
          top: 10,
          zIndex: 1013,
        }}
      >
        <CloseIcon onClick={closeSettings} />
      </div>
      {confirmationPopupOpen && (
        <UnsavedConfirmation
          confirm={close}
          cancel={() => setConfirmationPopupOpen(false)}
        />
      )}

      {isLoading && (
        <div
          style={{
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <LoadingIcon height={80} width={80} color="#005CA6" />
        </div>
      )}

      {!isLoading && (
        <>
          {/* BODY */}
          <div
            style={{
              overflowY: "auto",
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              padding: isMobile ? 10 : 32,
              gap: 30,
            }}
          >
            {/* FILTER OPTIONS */}
            <div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: "#595959",
                  paddingBottom: 10,
                }}
              >
                Default filters
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                <FormField label="UOM">
                  <>
                    <div
                      style={{
                        color: "#878787",
                        fontSize: 12,
                        fontStyle: "italic",
                      }}
                    >
                      Default UOM that will be used
                    </div>
                    <Select
                      style={{ width: 150 }}
                      size="small"
                      value={tempUom}
                      onChange={(e) => {
                        setTempUom(e.target.value);
                      }}
                      MenuProps={{ sx: { zIndex: 12020 } }}
                    >
                      <MenuItem value="" />
                      <MenuItem value="GAM">GAM</MenuItem>
                      <MenuItem value="MT">MT</MenuItem>
                      <MenuItem value="ST">ST</MenuItem>
                    </Select>
                  </>
                </FormField>
                <FormField label="Currency">
                  <>
                    <div
                      style={{
                        color: "#878787",
                        fontSize: 12,
                        fontStyle: "italic",
                      }}
                    >
                      Default Currency that will be used
                    </div>
                    <Select
                      style={{ width: 150 }}
                      size="small"
                      value={tempCurrency}
                      onChange={(e) => {
                        setTempCurrency(e.target.value);
                      }}
                      MenuProps={{ sx: { zIndex: 12020 } }}
                    >
                      <MenuItem value="" />
                      <MenuItem value="CAD">CAD</MenuItem>
                      <MenuItem value="USD">USD</MenuItem>
                    </Select>
                  </>
                </FormField>

                <FormField label="Products">
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        paddingBottom: 4,
                      }}
                    >
                      <div
                        style={{
                          color: "#878787",
                          fontSize: 12,
                          fontStyle: "italic",
                        }}
                      >
                        Favorite products will show at the top of the Products
                        dropdown. This is a required setting.
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 2,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 14,
                            color: "#989898",
                            fontWeight: 700,
                          }}
                        >
                          <FilterAlt />
                        </div>
                        <TextField
                          style={{ width: 130 }}
                          size="small"
                          value={productFilter}
                          onChange={(e) => setProductFilter(e.target.value)}
                        />
                      </div>
                    </div>

                    <TransferList
                      records={products}
                      setRecords={setProducts}
                      filter={productFilter}
                      onSelect={() => setProductFilter("")}
                    />
                  </>
                </FormField>
              </div>
            </div>
          </div>

          <div style={{ flexGrow: 1 }} />

          {/* FOOTER */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              padding: "20px 30px",
              borderTop: "1px solid #E0E0E0",
            }}
          >
            <Button
              id="filterOptionCancelButton"
              variant="outlined"
              color="neutral"
              onClick={closeSettings}
            >
              Cancel
            </Button>
            <Button
              id="filterOptionSaveButton"
              variant="contained"
              onClick={save}
            >
              Save
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default FilterOptions;
